import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Input, Modal, Select, Textarea } from "@legal-500/legal500-design-system";
import {
  aiParser,
  AI_PROVIDERS,
  AI_THINKING_LEVELS,
  type AiProvider,
  DATA_CLASSIFICATIONS,
  DATA_CLASSIFICATION_LABELS,
  type DataClassification,
  type AiProviderModels,
  type AiSchema,
  type AiThinkingLevel,
  type CreateAiSchemaInput,
} from "@/api/aiParser.service";
import { Toggle } from "@/components/Toggle/Toggle";
import { ModelField } from "./ModelField";
import styles from "./AiSchemas.module.css";

// Mirrors AiSchemaSlugSchema in submission-api: the slug becomes the output filename in the blob
// container (`<schema-slug>.json`), so anything needing escaping in a path is rejected here too
// rather than letting the save round-trip to a 400.
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

// A working two-field schema rather than an empty skeleton. The skeleton read as "fill this in",
// and the natural way to fill it in is to type field names where the cursor already is — at the
// top level, where JSON Schema ignores them. That produces a schema which saves, runs, and
// reports 100% success having extracted nothing. Starting from something shaped correctly makes
// the right place to add a field the obvious one.
const STARTER_JSON_SCHEMA = `{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "type": "object",
  "properties": {
    "firmName": {
      "type": ["string", "null"],
      "description": "The submitting firm's name, exactly as written in the document"
    },
    "matters": {
      "type": "array",
      "items": {
        "type": "object",
        "properties": {
          "clientName": { "type": ["string", "null"] },
          "description": { "type": ["string", "null"] }
        }
      }
    }
  },
  "required": ["firmName"]
}`;

type FormState = {
  name: string;
  slug: string;
  provider: AiProvider;
  model: string;
  thinkingLevel: "" | AiThinkingLevel;
  dataClassification: DataClassification;
  temperature: string;
  enabled: boolean;
  systemPrompt: string;
  jsonSchemaText: string;
};

function emptyForm(): FormState {
  return {
    name: "",
    slug: "",
    provider: "azure-openai",
    model: "",
    thinkingLevel: "",
    // Matches the column default. A new schema starts at the restrictive end and is relaxed
    // deliberately, rather than starting open and having to be noticed.
    dataClassification: "confidential",
    temperature: "",
    enabled: false,
    systemPrompt: "",
    jsonSchemaText: STARTER_JSON_SCHEMA,
  };
}

function formFromSchema(schema: AiSchema): FormState {
  return {
    name: schema.name,
    slug: schema.slug,
    provider: (schema.provider as AiProvider) ?? "azure-openai",
    model: schema.model,
    thinkingLevel: (schema.thinkingLevel as AiThinkingLevel | null) ?? "",
    dataClassification: schema.dataClassification,
    temperature: schema.temperature === null ? "" : String(schema.temperature),
    enabled: schema.enabled,
    systemPrompt: schema.systemPrompt ?? "",
    jsonSchemaText: JSON.stringify(schema.jsonSchema ?? {}, null, 2),
  };
}

type FieldErrors = Partial<Record<keyof FormState, string>>;

function validate(form: FormState): { errors: FieldErrors; jsonSchema: unknown } {
  const errors: FieldErrors = {};
  let jsonSchema: unknown = null;

  if (!form.name.trim()) errors.name = "Name is required";
  if (!form.slug.trim()) {
    errors.slug = "Slug is required";
  } else if (!SLUG_PATTERN.test(form.slug.trim())) {
    errors.slug = "Slug must be kebab-case: lower-case letters, digits and single hyphens";
  }
  if (!form.model.trim()) errors.model = "Model (Azure deployment name) is required";

  if (form.temperature.trim() !== "") {
    const parsed = Number(form.temperature);
    if (Number.isNaN(parsed)) {
      errors.temperature = "Temperature must be a number";
    } else if (parsed < 0 || parsed > 2) {
      errors.temperature = "Temperature must be between 0 and 2";
    }
  }

  // Client-side JSON parsing only. Whether the document is a *compilable* JSON Schema is
  // decided by Ajv in submission-api, which answers 400 with the compile error — that message
  // is surfaced on the form as the save error.
  try {
    const parsed: unknown = JSON.parse(form.jsonSchemaText);
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
      errors.jsonSchemaText = "The JSON Schema must be a JSON object";
    } else {
      jsonSchema = parsed;
    }
  } catch (error) {
    errors.jsonSchemaText = `Invalid JSON: ${error instanceof Error ? error.message : String(error)}`;
  }

  return { errors, jsonSchema };
}

/**
 * One labelled group of fields.
 *
 * `role="group"` + `aria-labelledby` rather than a fieldset: the semantics are the same, and a
 * plain div lays out as a flex column without the rendered-legend special cases that come with
 * putting `display: flex` on a fieldset.
 */
function FormSection({
  id,
  title,
  hint,
  children,
}: {
  id: string;
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.section} role="group" aria-labelledby={id}>
      <h3 id={id} className={styles.sectionTitle}>
        {title}
      </h3>
      {hint && <p className={styles.sectionHint}>{hint}</p>}
      {children}
    </div>
  );
}

type Props = {
  isOpen: boolean;
  schema: AiSchema | null;
  saving: boolean;
  saveError: string | null;
  onClose: () => void;
  onSubmit: (input: CreateAiSchemaInput) => void;
};

export function SchemaFormModal({ isOpen, schema, saving, saveError, onClose, onSubmit }: Props) {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<FieldErrors>({});

  // The model listing covers BOTH providers in one answer, so switching Provider swaps which list
  // the Model field shows without a second request. It is fetched once per mount of this component
  // (which outlives the dialog — Modal unmounts its children, this does not), and otherwise only
  // when the user asks for a refresh.
  const [providerModels, setProviderModels] = useState<AiProviderModels[] | null>(null);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [manualModel, setManualModel] = useState(false);
  const modelsRequested = useRef(false);

  const loadModels = useCallback(async (refresh: boolean) => {
    setModelsLoading(true);
    setModelsError(null);
    try {
      const response = await aiParser.listModels(refresh ? { refresh: true } : undefined);
      setProviderModels(response.providers);
    } catch (caught) {
      // A listing that will not load must not block the form: the Model field drops to free text
      // and says why, which is exactly what it does for a provider that answers available: false.
      setProviderModels([]);
      setModelsError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setModelsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen || modelsRequested.current) return;
    modelsRequested.current = true;
    void loadModels(false);
  }, [isOpen, loadModels]);

  useEffect(() => {
    if (!isOpen) return;
    setForm(schema ? formFromSchema(schema) : emptyForm());
    setErrors({});
    setManualModel(false);
  }, [isOpen, schema]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm(previous => ({ ...previous, [key]: value }));
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const { errors: nextErrors, jsonSchema } = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    onSubmit({
      name: form.name.trim(),
      slug: form.slug.trim(),
      jsonSchema,
      systemPrompt: form.systemPrompt.trim() === "" ? null : form.systemPrompt,
      provider: form.provider,
      model: form.model.trim(),
      thinkingLevel: form.thinkingLevel === "" ? null : form.thinkingLevel,
      dataClassification: form.dataClassification,
      temperature: form.temperature.trim() === "" ? null : Number(form.temperature),
      enabled: form.enabled,
    });
  }

  const selectedProviderModels =
    providerModels?.find(entry => entry.provider === form.provider) ?? null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={schema ? `Edit ${schema.name}` : "New AI schema"}
      size="large"
      backdropClick={false}
    >
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        {/* Four labelled groups rather than one flat grid. Help text lives under the group, not
            under each field, so every control in a row starts and ends on the same line — only a
            validation error, which is per-field by nature, ever changes a field's height. */}
        <FormSection
          id="ai-schema-section-identity"
          title="Identity"
          hint="What this schema is called, and the slug that becomes the output filename in the blob container."
        >
          <div className={styles.fieldGrid}>
            <Input
              label="Name"
              value={form.name}
              onChange={event => set("name", event.target.value)}
              error={errors.name}
              fullWidth
            />
            <Input
              label="Slug"
              value={form.slug}
              onChange={event => set("slug", event.target.value)}
              error={errors.slug}
              fullWidth
            />
            <Select
              label="Data classification"
              value={form.dataClassification}
              onChange={event =>
                set("dataClassification", event.target.value as DataClassification)
              }
              options={DATA_CLASSIFICATIONS.map(value => ({
                value,
                label: DATA_CLASSIFICATION_LABELS[value],
              }))}
              fullWidth
            />
          </div>
          <ul className={styles.sectionNotes}>
            <li>
              <strong>Data classification</strong> — how sensitive this schema's extracted JSON is.
              The worker writes it into a folder of that name inside the run, so it is a path prefix
              an access rule can be written against, not just a label. A markdown template carries
              its own, because a summary is often safe to circulate when the extraction behind it is
              not.
            </li>
          </ul>
        </FormSection>

        {/* Titled "Model and runtime", not "Model": a group whose accessible name is exactly one
            of its own field labels is ambiguous to a screen reader, and to anything else that
            looks a control up by name. */}
        <FormSection
          id="ai-schema-section-model"
          title="Model and runtime"
          hint="How this schema runs, every time it runs."
        >
          <div className={styles.fieldGrid}>
            <Select
              label="Provider"
              value={form.provider}
              onChange={event => set("provider", event.target.value as AiProvider)}
              options={AI_PROVIDERS.map(provider => ({
                value: provider.value,
                label: provider.label,
              }))}
              fullWidth
            />
            <ModelField
              value={form.model}
              onChange={next => set("model", next)}
              error={errors.model}
              providerModels={selectedProviderModels}
              loading={modelsLoading}
              loadError={modelsError}
              manual={manualModel}
              onManualChange={setManualModel}
              onRefresh={() => void loadModels(true)}
            />
            <Select
              label="Thinking level"
              value={form.thinkingLevel}
              onChange={event => set("thinkingLevel", event.target.value as "" | AiThinkingLevel)}
              options={[
                { value: "", label: "Unset — send no thinking control" },
                ...AI_THINKING_LEVELS.map(level => ({ value: level, label: level })),
              ]}
              fullWidth
            />
            <Input
              label="Temperature"
              type="number"
              step="0.1"
              min="0"
              max="2"
              value={form.temperature}
              onChange={event => set("temperature", event.target.value)}
              error={errors.temperature}
              fullWidth
            />
          </div>
          <ul className={styles.sectionNotes}>
            <li>
              <strong>Thinking level</strong> — <em>Unset</em> sends no thinking control at all,
              which is what a non-reasoning model needs. It is not the same as <em>none</em>.
            </li>
            <li>
              <strong>Temperature</strong> — leave blank to send none. Reasoning models ignore it.
            </li>
          </ul>
        </FormSection>

        {/* The single most consequential control on the form, so it keeps a callout of its own
            rather than becoming a checkbox in a grid. */}
        <FormSection id="ai-schema-section-status" title="Status">
          <div className={form.enabled ? styles.enableCalloutOn : styles.enableCallout}>
            <div className={styles.enableCalloutRow}>
              <Toggle
                checked={form.enabled}
                onChange={next => set("enabled", next)}
                ariaLabel="Enabled"
              />
              <div>
                <p className={styles.enableCalloutTitle}>
                  {form.enabled ? "Enabled — this schema runs and costs money" : "Disabled"}
                </p>
                <p className={styles.enableCalloutBody}>
                  An enabled schema runs against <strong>every</strong> submission document, on top
                  of every other enabled schema. Each run is a paid model call. Disabling is how you
                  take a schema out of the rotation; deleting is not possible once it has runs.
                </p>
              </div>
            </div>
          </div>
        </FormSection>

        <FormSection
          id="ai-schema-section-extraction"
          title="Extraction"
          hint="What the model is asked for. The JSON Schema is checked here for valid JSON, and compiled with Ajv on save."
        >
          <Textarea
            label="System prompt override"
            value={form.systemPrompt}
            onChange={event => set("systemPrompt", event.target.value)}
            rows={3}
            className={styles.monospaceField}
            helperText="Optional. Leave blank to use the worker's default extraction prompt."
            fullWidth
          />
          {/* Pasted once and rarely touched, so it gets a working height rather than the whole
              dialog — and a drag handle for the times it does need editing in place. */}
          <Textarea
            label="JSON Schema"
            value={form.jsonSchemaText}
            onChange={event => set("jsonSchemaText", event.target.value)}
            rows={9}
            spellCheck={false}
            resize="vertical"
            className={`${styles.monospaceField} ${styles.jsonSchemaField}`}
            error={errors.jsonSchemaText}
            helperText="A 2020-12 JSON Schema object. Field names go inside `properties` — anything at the top level that is not a JSON Schema keyword is ignored, and a schema with no fields is rejected on save. Drag the bottom-right corner to make it taller."
            fullWidth
          />
        </FormSection>

        {saveError && (
          <div className={styles.errorBanner} role="alert">
            {saveError}
          </div>
        )}

        <div className={styles.formActions}>
          <Button type="button" variant="outlined" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? "Saving…" : schema ? "Save changes" : "Create schema"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
